import React from "react"
import { useEditor } from "@layerhub-io/react"
import { useStyletron } from "baseui"
import { Block } from "baseui/block"
import AngleDoubleLeft from "~/components/Icons/AngleDoubleLeft"
import Scrollable from "~/components/Scrollable"
import useSetIsSidebarOpen from "~/hooks/useSetIsSidebarOpen"

// Shape definitions with paths and preview colors
const shapes = [
    {
        name: "Rectangle",
        type: "StaticPath",
        path: [[["M", 0, 0], ["L", 100, 0], ["L", 100, 100], ["L", 0, 100], ["Z"]]],
        width: 100,
        height: 100,
        fill: "#4A90D9",
        preview: "#4A90D9",
    },
    {
        name: "Circle",
        type: "StaticPath",
        path: [[
            ["M", 50, 0],
            ["C", 77.614, 0, 100, 22.386, 100, 50],
            ["C", 100, 77.614, 77.614, 100, 50, 100],
            ["C", 22.386, 100, 0, 77.614, 0, 50],
            ["C", 0, 22.386, 22.386, 0, 50, 0],
            ["Z"]
        ]],
        width: 100,
        height: 100,
        fill: "#E74C3C",
        preview: "#E74C3C",
    },
    {
        name: "Triangle",
        type: "StaticPath",
        path: [[["M", 50, 0], ["L", 100, 100], ["L", 0, 100], ["Z"]]],
        width: 100,
        height: 100,
        fill: "#2ECC71",
        preview: "#2ECC71",
    },
    {
        name: "Star",
        type: "StaticPath",
        path: [[
            ["M", 50, 0],
            ["L", 61.8, 38.2],
            ["L", 100, 38.2],
            ["L", 69.1, 61.8],
            ["L", 80.9, 100],
            ["L", 50, 76.4],
            ["L", 19.1, 100],
            ["L", 30.9, 61.8],
            ["L", 0, 38.2],
            ["L", 38.2, 38.2],
            ["Z"]
        ]],
        width: 100,
        height: 100,
        fill: "#F1C40F",
        preview: "#F1C40F",
    },
    {
        name: "Heart",
        type: "StaticPath",
        path: [[
            ["M", 50, 90],
            ["C", 10, 60, 5, 30, 25, 15],
            ["C", 40, 5, 50, 20, 50, 30],
            ["C", 50, 20, 60, 5, 75, 15],
            ["C", 95, 30, 90, 60, 50, 90],
            ["Z"]
        ]],
        width: 100,
        height: 100,
        fill: "#E91E63",
        preview: "#E91E63",
    },
    {
        name: "Hexagon",
        type: "StaticPath",
        path: [[
            ["M", 50, 0],
            ["L", 93.3, 25],
            ["L", 93.3, 75],
            ["L", 50, 100],
            ["L", 6.7, 75],
            ["L", 6.7, 25],
            ["Z"]
        ]],
        width: 100,
        height: 100,
        fill: "#9B59B6",
        preview: "#9B59B6",
    },
    {
        name: "Pentagon",
        type: "StaticPath",
        path: [[
            ["M", 50, 0],
            ["L", 97.5, 34.5],
            ["L", 79.4, 90.5],
            ["L", 20.6, 90.5],
            ["L", 2.5, 34.5],
            ["Z"]
        ]],
        width: 100,
        height: 100,
        fill: "#3498DB",
        preview: "#3498DB",
    },
    {
        name: "Diamond",
        type: "StaticPath",
        path: [[["M", 50, 0], ["L", 100, 50], ["L", 50, 100], ["L", 0, 50], ["Z"]]],
        width: 100,
        height: 100,
        fill: "#1ABC9C",
        preview: "#1ABC9C",
    },
    {
        name: "Arrow Right",
        type: "StaticPath",
        path: [[
            ["M", 0, 35],
            ["L", 60, 35],
            ["L", 60, 15],
            ["L", 100, 50],
            ["L", 60, 85],
            ["L", 60, 65],
            ["L", 0, 65],
            ["Z"]
        ]],
        width: 100,
        height: 100,
        fill: "#E67E22",
        preview: "#E67E22",
    },
    {
        name: "Plus",
        type: "StaticPath",
        path: [[
            ["M", 40, 0],
            ["L", 60, 0],
            ["L", 60, 40],
            ["L", 100, 40],
            ["L", 100, 60],
            ["L", 60, 60],
            ["L", 60, 100],
            ["L", 40, 100],
            ["L", 40, 60],
            ["L", 0, 60],
            ["L", 0, 40],
            ["L", 40, 40],
            ["Z"]
        ]],
        width: 100,
        height: 100,
        fill: "#95A5A6",
        preview: "#95A5A6",
    },
    {
        name: "Rounded Rectangle",
        type: "StaticPath",
        path: [[
            ["M", 20, 0],
            ["L", 80, 0],
            ["C", 91, 0, 100, 9, 100, 20],
            ["L", 100, 80],
            ["C", 100, 91, 91, 100, 80, 100],
            ["L", 20, 100],
            ["C", 9, 100, 0, 91, 0, 80],
            ["L", 0, 20],
            ["C", 0, 9, 9, 0, 20, 0],
            ["Z"]
        ]],
        width: 100,
        height: 100,
        fill: "#34495E",
        preview: "#34495E",
    },
    {
        name: "Octagon",
        type: "StaticPath",
        path: [[
            ["M", 30, 0],
            ["L", 70, 0],
            ["L", 100, 30],
            ["L", 100, 70],
            ["L", 70, 100],
            ["L", 30, 100],
            ["L", 0, 70],
            ["L", 0, 30],
            ["Z"]
        ]],
        width: 100,
        height: 100,
        fill: "#C0392B",
        preview: "#C0392B",
    },
]

// Random color palette for shapes
const colorPalette = [
    "#4A90D9", "#E74C3C", "#2ECC71", "#F1C40F", "#E91E63",
    "#9B59B6", "#3498DB", "#1ABC9C", "#E67E22", "#95A5A6",
    "#34495E", "#C0392B", "#27AE60", "#8E44AD", "#2980B9"
]

function getRandomColor() {
    return colorPalette[Math.floor(Math.random() * colorPalette.length)]
}

export default function () {
    const editor = useEditor()
    const setIsSidebarOpen = useSetIsSidebarOpen()
    const [css] = useStyletron()

    const addShape = React.useCallback(
        (shape: typeof shapes[0]) => {
            if (editor) {
                const randomColor = getRandomColor()
                const shapeToAdd = {
                    type: shape.type,
                    path: shape.path[0],
                    fill: randomColor,
                    width: shape.width,
                    height: shape.height,
                    scaleX: 2,
                    scaleY: 2,
                }
                editor.objects.add(shapeToAdd)
            }
        },
        [editor]
    )

    return (
        <Block $style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Block
                $style={{
                    display: "flex",
                    alignItems: "center",
                    fontWeight: 500,
                    justifyContent: "space-between",
                    padding: "1.5rem",
                }}
            >
                <Block>Shapes</Block>

                <Block onClick={() => setIsSidebarOpen(false)} $style={{ cursor: "pointer", display: "flex" }}>
                    <AngleDoubleLeft size={18} />
                </Block>
            </Block>
            <Scrollable>
                <Block>
                    <Block $style={{ display: "grid", gap: "8px", padding: "1.5rem", gridTemplateColumns: "1fr 1fr 1fr" }}>
                        {shapes.map((shape, index) => (
                            <ShapeItem
                                key={index}
                                shape={shape}
                                onClick={() => addShape(shape)}
                            />
                        ))}
                    </Block>
                </Block>
            </Scrollable>
        </Block>
    )
}

function ShapeItem({ shape, onClick }: { shape: typeof shapes[0]; onClick: () => void }) {
    const [css] = useStyletron()

    return (
        <div
            onClick={onClick}
            className={css({
                position: "relative",
                height: "60px",
                background: "#f8f8fb",
                cursor: "pointer",
                borderRadius: "8px",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
                ":hover": {
                    background: "rgb(233,233,233)",
                    transform: "scale(1.05)",
                },
            })}
        >
            <div
                className={css({
                    width: "40px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                })}
            >
                <ShapePreview shape={shape} />
            </div>
        </div>
    )
}

function ShapePreview({ shape }: { shape: typeof shapes[0] }) {
    const [css] = useStyletron()

    // Render different SVG shapes based on shape name
    const renderShape = () => {
        switch (shape.name) {
            case "Rectangle":
                return <rect x="2" y="2" width="32" height="32" fill={shape.preview} />
            case "Circle":
                return <circle cx="18" cy="18" r="16" fill={shape.preview} />
            case "Triangle":
                return <polygon points="18,2 34,34 2,34" fill={shape.preview} />
            case "Star":
                return <polygon points="18,2 22,14 34,14 25,22 28,34 18,27 8,34 11,22 2,14 14,14" fill={shape.preview} />
            case "Heart":
                return (
                    <path
                        d="M18 32 C4 20 2 10 9 5 C14 2 18 8 18 12 C18 8 22 2 27 5 C34 10 32 20 18 32 Z"
                        fill={shape.preview}
                    />
                )
            case "Hexagon":
                return <polygon points="18,2 32,10 32,26 18,34 4,26 4,10" fill={shape.preview} />
            case "Pentagon":
                return <polygon points="18,2 34,14 29,34 7,34 2,14" fill={shape.preview} />
            case "Diamond":
                return <polygon points="18,2 34,18 18,34 2,18" fill={shape.preview} />
            case "Arrow Right":
                return <polygon points="2,12 22,12 22,4 34,18 22,32 22,24 2,24" fill={shape.preview} />
            case "Plus":
                return (
                    <path
                        d="M14,2 L22,2 L22,14 L34,14 L34,22 L22,22 L22,34 L14,34 L14,22 L2,22 L2,14 L14,14 Z"
                        fill={shape.preview}
                    />
                )
            case "Rounded Rectangle":
                return <rect x="2" y="2" width="32" height="32" rx="6" fill={shape.preview} />
            case "Octagon":
                return <polygon points="12,2 24,2 34,12 34,24 24,34 12,34 2,24 2,12" fill={shape.preview} />
            default:
                return <rect x="2" y="2" width="32" height="32" fill={shape.preview} />
        }
    }

    return (
        <svg
            width="36"
            height="36"
            viewBox="0 0 36 36"
            className={css({ pointerEvents: "none" })}
        >
            {renderShape()}
        </svg>
    )
}
